import { useEffect, useRef, useState } from "react";
import { formatFrenchDay } from "../lib/days";

export type FieldOption = { value: string; label: string };

export function foldText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();
}

export function matchesQuery(haystack: string, query: string): boolean {
  const needle = foldText(query.trim());
  if (!needle) return true;
  return foldText(haystack).includes(needle);
}

export function SearchField({
  value,
  onChange,
  placeholder = "Rechercher",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className="pr-input"
      onKeyDown={(e) => {
        if (e.key === "Enter") e.preventDefault();
      }}
    />
  );
}

function useDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose]);
  return ref;
}

export function SelectField({
  value,
  options,
  onChange,
  placeholder = "Choisir",
}: {
  value: string;
  options: FieldOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useDismiss(open, () => setOpen(false));
  const current = options.find((o) => o.value === value);
  const searchable = options.length > 7;
  const shown = searchable ? options.filter((o) => matchesQuery(o.label, query)) : options;

  function close() {
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={ref} className="pr-field">
      <button type="button" className={`pr-trigger ${open ? "is-open" : ""}`} onClick={() => (open ? close() : setOpen(true))}>
        <span className={current ? "" : "text-slate-400"}>{current?.label || placeholder}</span>
        <span className="pr-chevron" aria-hidden />
      </button>
      {open ? (
        <ul className="pr-menu">
          {searchable ? (
            <li className="pr-menu-search">
              <SearchField value={query} onChange={setQuery} placeholder="Filtrer" />
            </li>
          ) : null}
          {shown.length === 0 ? (
            <li className="px-3 py-3 text-sm text-slate-400">Aucun résultat</li>
          ) : (
            shown.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  className={`pr-menu-item ${o.value === value ? "is-on" : ""}`}
                  onClick={() => {
                    onChange(o.value);
                    close();
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function labelDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Choisir une date";
  return formatFrenchDay(value);
}

export function DateField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const base = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + "T12:00:00") : new Date();
  const [cursor, setCursor] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const first = new Date(cursor.y, cursor.m, 1);
  const start = (first.getDay() + 6) % 7;
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const today = ymd(new Date());

  return (
    <div ref={ref} className="pr-field">
      <button type="button" className={`pr-trigger ${open ? "is-open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <span>{labelDate(value)}</span>
        <span className="pr-chevron" aria-hidden />
      </button>
      {open ? (
        <div className="pr-cal">
          <div className="mb-3 flex items-center justify-between">
            <button type="button" className="pr-cal-nav" onClick={() => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }))}>
              ‹
            </button>
            <p className="text-sm font-semibold capitalize">
              {MONTHS[cursor.m]} {cursor.y}
            </p>
            <button type="button" className="pr-cal-nav" onClick={() => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }))}>
              ›
            </button>
          </div>
          <div className="pr-cal-grid text-[11px] text-slate-400">
            {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
              <span key={i} className="py-1 text-center">
                {d}
              </span>
            ))}
          </div>
          <div className="pr-cal-grid mt-1">
            {Array.from({ length: start }, (_, i) => (
              <span key={`p${i}`} />
            ))}
            {Array.from({ length: days }, (_, i) => {
              const date = ymd(new Date(cursor.y, cursor.m, i + 1));
              return (
                <button
                  key={date}
                  type="button"
                  className={`pr-cal-day ${date === value ? "is-on" : ""} ${date === today ? "is-today" : ""}`}
                  onClick={() => {
                    onChange(date);
                    setOpen(false);
                  }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function timeOptions(): FieldOption[] {
  const out: FieldOption[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      out.push({ value, label: value.replace(":", " h ") });
    }
  }
  return out;
}

const TIMES = timeOptions();

export function TimeField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const opts = TIMES.some((t) => t.value === value) ? TIMES : [{ value, label: value.replace(":", " h ") }, ...TIMES];
  return <SelectField value={value} options={opts} onChange={onChange} placeholder="Heure" />;
}
